import { isEqual, isEmpty } from 'lodash';
import { useMemo, useRef } from 'react';
import { ColDef } from 'ag-grid-community';

import { ViewColumn } from 'common/types/viewColumn';
import { HeaderFormatStyle, TableColumnFormat, UniqueValuesData } from '../types';
import { CustomSortConfig, Hierarchy, HierarchyColumnConfig, OrderConfig } from 'common/visualizations/vif';
import { ColumnAggregation } from 'common/visualizations/dataProviders/types';
import computeAgColumn from '../helpers/computeAgColumn';
import { ISortIcons } from '../helpers/sortIconHelpers';

type ColDefPropertyKey = keyof ColDef;
const GRID_CONTROLLED_PROP_KEYS: ColDefPropertyKey[] = [
  'flex',
  'sort',
  'sortIndex',
  'sortable',
  'cellRenderer',
  'headerClass',
  'cellStyle',
  'tooltipValueGetter',
  'valueGetter',
  'cellClass',
  'cellClassRules',
];

const areColDefsEquivalent = (a: ColDef | undefined, b: ColDef | undefined) => {
  if (a === b) {
    return true;
  }
  const keysA = (a ? Object.keys(a) : []) as Array<keyof ColDef>;
  const keysB = (b ? Object.keys(b) : []) as Array<keyof ColDef>;

  const localSkipKeys = [...GRID_CONTROLLED_PROP_KEYS];

  for (const key of keysA) {
    if (localSkipKeys.includes(key as ColDefPropertyKey)) {
      continue;
    }
    if (!keysB.includes(key)) {
      return false;
    }
    if ([null, undefined].includes(a?.[key]) && [null, undefined].includes(b?.[key])) {
      continue;
    }
    if (!isEqual(a?.[key], b?.[key])) {
      return false;
    }
  }
  return true;
};

/**
 * Determines if the column definitions should be recreated by comparing the two lists of column definitions.
 * The comparison takes into account the order of the columns and the properties of each column.
 * @param listA
 * @param listB
 */
const shouldRecreateColDefs = (listA: ColDef[], listB: ColDef[]) => {
  if (listA.length !== listB.length) return true;

  for (let i = 0; i < listA.length; i++) {
    const column = listA[i];
    const b = listB[i];
    if (!areColDefsEquivalent(column, b)) return true;
  }

  return false;
};

interface IUseColDefsParams {
  columns: ViewColumn[];
  columnMetadata: ViewColumn[];
  columnFormats: { [key: string]: TableColumnFormat };
  datasetUid: string;
  displayColumnFilters?: boolean;
  domain: string;
  headerFormat: HeaderFormatStyle;
  hierarchyConfig?: Hierarchy;
  hierarchyColumns: HierarchyColumnConfig[];
  nonStandardAggregations?: ColumnAggregation[] | null;
  removedHierarchyColumnNames: Set<string>;
  showAgGridColumnAggregations?: boolean;
  showAgGridColumnMenu?: boolean;
  useSetFilters?: boolean;
  vifOrderConfig: OrderConfig[];
  actionColumnDef?: ColDef;
  showCustomSortMenuItems?: boolean;
  setIsCustomSortModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedColumns?: ViewColumn[];
  setSelectedColumns?: React.Dispatch<React.SetStateAction<ViewColumn[]>>;
  getUniqueValues?: (
    columns: ViewColumn[],
    order: OrderConfig[],
    customSortConfig: CustomSortConfig | undefined,
    limit?: number,
  ) => Promise<UniqueValuesData>;
  getSortIcons: ISortIcons;
  setCurrentSortState?: React.Dispatch<React.SetStateAction<OrderConfig[]>>;
  handleCustomSortCycle?: (newOrderConfig: OrderConfig[], hierarchyId?: string) => void;
  handleColumnPinnedChange?: (columnName: string, pinned: boolean) => void;
}
/**
 * useColDefs will return the same object reference as long as only column properties that are controlled
 * by AG Grid have been changed. Since AG Grid will automatically rerender itself when these properties change,
 * there's no need to cause a second rerender by passing a new object to the AgGridReact component's props.
 *
 * Preventing extra rerenders allows us to preserve most ephemeral changes the user has made to the table, and
 * generally reducing rerenders improves performance a little bit.
 *
 * @see GRID_CONTROLLED_PROP_KEYS for which properties are considered socrata controlled vs AG Grid controlled.
 *
 * @param props These are the parameters to computeAgColumn, and also the reactive values for the useMemo
 * @returns an array of ColDefs for AG Grid React's columnDefs prop
 */
const useColDefs = ({
  columns,
  columnMetadata,
  columnFormats,
  datasetUid,
  displayColumnFilters,
  domain,
  headerFormat,
  hierarchyConfig,
  hierarchyColumns,
  nonStandardAggregations,
  removedHierarchyColumnNames,
  showAgGridColumnAggregations,
  showAgGridColumnMenu,
  useSetFilters,
  vifOrderConfig,
  actionColumnDef,
  showCustomSortMenuItems,
  selectedColumns,
  setIsCustomSortModalOpen,
  setSelectedColumns,
  getUniqueValues,
  getSortIcons,
  setCurrentSortState,
  handleCustomSortCycle,
  handleColumnPinnedChange,
}: IUseColDefsParams) => {
  const columnsRef = useRef<Array<ColDef>>([]);

  return useMemo(() => {
    const agColumns = columns
      .map((column) => {
        const viewColumnMetadata = findColumnMetadata(column, columnMetadata) ?? column;
        return computeAgColumn({
          column,
          viewColumnMetadata,
          columnFormats,
          datasetUid,
          displayColumnFilters,
          domain,
          headerFormat,
          hierarchyColumns,
          hierarchyConfig,
          nonStandardAggregations,
          removedHierarchyColumnNames,
          showAgGridColumnAggregations,
          showAgGridColumnMenu,
          useSetFilters,
          vifOrderConfig,
          showCustomSortMenuItems,
          columns,
          selectedColumns,
          setIsCustomSortModalOpen,
          setSelectedColumns,
          getUniqueValues,
          columnMetadata,
          getSortIcons,
          setCurrentSortState,
          handleCustomSortCycle,
          handleColumnPinnedChange,
        });
      })
      .filter((column): column is ColDef => column !== null);
    if (actionColumnDef && !isEmpty(actionColumnDef)) {
      agColumns.push(actionColumnDef);
    }
    if (shouldRecreateColDefs(columnsRef.current, agColumns)) {
      columnsRef.current = agColumns;
    }

    return columnsRef.current;
  }, [
    columns,
    columnMetadata,
    columnFormats,
    datasetUid,
    displayColumnFilters,
    domain,
    hierarchyConfig,
    hierarchyColumns,
    nonStandardAggregations,
    removedHierarchyColumnNames,
    showAgGridColumnAggregations,
    showAgGridColumnMenu,
    useSetFilters,
    vifOrderConfig,
    selectedColumns,
    setIsCustomSortModalOpen,
    setSelectedColumns,
    getUniqueValues,
    getSortIcons,
  ]);
};

export const findColumnMetadata = (
  { fieldName }: Pick<ViewColumn, 'fieldName'>,
  colMetadata: ViewColumn[],
) => {
  return colMetadata.find((metadata) => metadata.fieldName === fieldName);
};

export default useColDefs;
